import React, { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  formatAdminDisplayDate,
  getAdminMaxSelectableDate,
  getLocalISODate,
} from "../utils";
import { DesktopIcon, MonitorIcon } from "./Shared";

const SectionBookingsList = ({
  sectionBookings,
  selectedSection,
  handleCancelBooking,
  selectedSeatId,
  users = [],
  filterDate,
  loadSectionBookings,
}) => {

  const [selectedDate, setSelectedDate] = useState(() => {
    return filterDate || getLocalISODate(new Date());
  });

  const normalizeValue = (value) =>
    typeof value === "string" ? value.trim().toLowerCase() : "";

  const isBlockedBooking = (booking = {}) =>
    normalizeValue(booking?.booking_details?.restriction_type || booking?.restrictionType) === "block" ||
    normalizeValue(booking?.source) === "seatblock" ||
    (normalizeValue(booking?.bookingType) === "block" && normalizeValue(booking?.userName) === "n/a");

  const getBookingTypeStyles = (bookingType = "") => {
    const normalizedType = normalizeValue(bookingType);
    if (normalizedType === "permanent") return "bg-indigo-100 text-indigo-600 border border-indigo-100";
    if (normalizedType === "recurring") return "bg-amber-100 text-amber-600 border border-amber-100";
    if (normalizedType === "date-range" || normalizedType === "date range") {
      return "bg-emerald-100 text-emerald-600 border border-emerald-100";
    }
    if (normalizedType === "one-time" || normalizedType === "onetime") {
      return "bg-sky-100 text-sky-600 border border-sky-100";
    }
    return "bg-slate-100 text-slate-600 border border-slate-100";
  };
  React.useEffect(() => {
    if (selectedSection?.id) {
      loadSectionBookings({ sectionId: selectedSection.id, date: selectedDate });
    }
  }, [selectedDate, selectedSection?.id, loadSectionBookings]);

  const maxSelectableDate = getAdminMaxSelectableDate(new Date());

  const visibleBookings = sectionBookings
    .filter((b) => {
      const isActive = b.status === "active";
      const matchesSeat = !selectedSeatId || b.seatId === selectedSeatId;
      const isPermanent = b.date === "permanent";
      const matchesDate = b.date === selectedDate;
      return isActive && matchesSeat && (isPermanent || matchesDate);
    })
    .sort((a, b) => {
      if (a.date === "permanent") return -1;
      if (b.date === "permanent") return 1;
      const da = a.date || "";
      const db = b.date || "";
      return da.localeCompare(db);
    });

  const groupedByDate = visibleBookings.reduce((acc, booking) => {
    const dateLabel =
      booking.date === "permanent"
        ? "Permanent Assignment"
        : "Booked for " + formatAdminDisplayDate(booking.date);
    if (!acc[dateLabel]) acc[dateLabel] = [];
    acc[dateLabel].push(booking);
    return acc;
  }, {});

  return (
    <div className=" border border-slate-200 bg-slate-50 p-5">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-950">Section Bookings</h3>
        <div className="admin-custom-date-picker">
          <DatePicker
            selected={selectedDate ? new Date(`${selectedDate}T00:00:00`) : null}
            onChange={(date) => {
              if (date) setSelectedDate(getLocalISODate(date));
            }}
            minDate={new Date()}
            maxDate={maxSelectableDate}
            filterDate={(date) => date.getDay() !== 0 && date.getDay() !== 6}
            dateFormat="dd-MM-yyyy"
            customInput={
              <input className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200" />
            }
          />
        </div>
      </div>
      <div className="mt-4 space-y-6">
        {Object.keys(groupedByDate).length === 0 && (
          <p className="text-sm text-slate-500 text-center py-4">
            No active bookings in this section.
          </p>
        )}
        {Object.entries(groupedByDate).map(([date, bookings]) => (
          <div key={date} className="space-y-3">
            <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1 flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-slate-300"></span>
              {date}
            </h4>
            {bookings.map((booking) => {
              const seat = selectedSection.seats.find(
                (item) => item.id === booking.seatId
              );
              const resolvedSeatLabel = seat?.label || booking.seatLabel || "—";
              const user = users.find((u) => u.id === booking.bookedFor);
              const blocked = isBlockedBooking(booking);
              const bookedByName = blocked
                ? "Blocked"
                : booking.userName || user?.fullName || booking.bookedFor;
              const isPermanent = booking.date === "permanent";
              const bookingTypeLabel = booking.bookingType || "One-Time";
              return (
                <div
                  key={booking.id}
                  className={`rounded-2xl border bg-white p-3 shadow-sm transition-all hover:shadow-md ${
                    isPermanent ? "border-violet-100" : "border-slate-100"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                        Booked For
                      </p>
                      <p className="mt-1 text-sm font-bold text-slate-900">
                        {bookedByName}
                      </p>
                      <div className="mt-1 flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-slate-500 font-medium">
                          Seat {resolvedSeatLabel}
                        </span>
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[8px] font-bold uppercase tracking-wide ${getBookingTypeStyles(bookingTypeLabel)}`}>
                          {bookingTypeLabel}
                        </span>
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize tracking-wide ${
                          (booking.seatType || seat?.type || "laptop").toLowerCase() === "monitor"
                            ? "bg-blue-50 text-blue-700 border border-blue-100"
                            : (booking.seatType || seat?.type || "laptop").toLowerCase() === "desktop"
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                            : "bg-amber-50 text-amber-700 border border-amber-100"
                        }`}>
                          {(booking.seatType || seat?.type || "laptop").toLowerCase() === "monitor" ? (
                            <MonitorIcon className="w-3 h-3" />
                          ) : (booking.seatType || seat?.type || "laptop").toLowerCase() === "desktop" ? (
                            <DesktopIcon className="w-3 h-3" />
                          ) : (
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><rect x="2" y="4" width="20" height="14" rx="2" /><path d="M2 18h20" /></svg>
                          )}
                          {(booking.seatType || seat?.type || "laptop").toLowerCase()}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleCancelBooking(booking.id, booking.bookingType)}
                      className="rounded-full border border-rose-100 px-3 py-1.5 text-[10px] font-bold text-rose-600 hover:bg-rose-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SectionBookingsList;
